import {Map} from 'immutable';
import constants from 'client/constants';
import type {AuthoringSupplement as AuthoringSupplementModel} from 'resources/FrozenModels/AuthoringSupplement/AuthoringSupplementModel.v1';
import type {Supplement as SupplementModel} from 'resources/GeneratedModels/Supplement/SupplementModel.v1';

type Location = {
  path: string;
  host: string;
  href: string;
};

/**
 * Shape of the model instance these extensions are mixed into. The extensions
 * rely on methods provided by the base Supplement/AuthoringSupplement models.
 */
interface BaseSupplementContext {
  getContent(): Map<string, unknown> | null;
  getType(): string;
  setField(field: string, value: unknown): AuthoringSupplementModel | SupplementModel;
}

const requiredAttributesByType: Record<string, string[]> = {
  audio: ['audio'],
  'free-form': ['text'],
  image: ['image'],
  passage: ['text'],
  'sortable-table': ['text']
};

/** Immutable 3.8's `Map.isMap` is not declared as a type guard, so narrow through this. */
function isContentMap(content: Map<string, unknown> | null): content is Map<string, unknown> {
  return Map.isMap(content);
}

/**
 * The API sends `content: null` for a supplement the actor may not read. Reads coerce it so a
 * withheld supplement cannot throw mid-render; an empty map reads the same as an absent key.
 */
function readContent(model: BaseSupplementContext): Map<string, unknown> {
  const content = model.getContent();
  return isContentMap(content) ? content : Map();
}

/**
 * Writes refuse withheld content rather than inventing a map: `setField('content', …)` PATCHes the
 * whole value, so building from an empty map would erase the content the server withheld from us.
 * An empty-but-present map is a legitimate starting point for a supplement being authored.
 */
function writeContent(model: BaseSupplementContext): Map<string, unknown> {
  const content = model.getContent();
  if (!isContentMap(content)) {
    throw new Error('Supplement content was withheld or never loaded; refusing to modify it');
  }
  return content;
}

export const baseSupplementExtensions = {
  /**
   * False when there is nothing to render: the API withheld the content (`null` over JSON:API) or
   * returned none (`{}` — how the GQL adapter normalizes an absent one). Deliberately stricter than
   * `readContent`: no live supplement has empty content, so empty only ever means "not delivered".
   */
  hasReadableContent(this: BaseSupplementContext): boolean {
    const content = this.getContent();
    return isContentMap(content) && !content.isEmpty();
  },

  /** Content coerced to a Map, for consumers that render it directly. */
  getReadableContent(this: BaseSupplementContext): Map<string, unknown> {
    return readContent(this);
  },

  getAlttext(this: BaseSupplementContext): string {
    return readContent(this).get('alttext') as string;
  },

  getAudio(this: BaseSupplementContext): string {
    return readContent(this).get('audio') as string;
  },

  getCaption(this: BaseSupplementContext): string {
    return readContent(this).get('caption') as string;
  },

  getCropValues(this: BaseSupplementContext): Map<string, unknown> {
    const keySet = ['x', 'y', 'width', 'height'];
    // Immutable 3.8 typings declare Map.filter as returning the broader
    // `Iterable<K, V>`, but at runtime filtering a Map yields a Map.
    return readContent(this).filter(
      (value, key) => key !== undefined && keySet.includes(key)
    ) as Map<string, unknown>;
  },

  getExcludeWhiteSpace(this: BaseSupplementContext): boolean {
    return readContent(this).get('excludeWhiteSpace') as boolean;
  },

  getHasTotal(this: BaseSupplementContext): boolean {
    return readContent(this).get('hasTotal') as boolean;
  },

  getHideLineNumbers(this: BaseSupplementContext): boolean {
    return readContent(this).get('hideLineNumbers') as boolean;
  },

  getIgnoreWhiteSpace(this: BaseSupplementContext): boolean {
    return readContent(this).get('ignoreWhiteSpace') as boolean;
  },

  /**
   * `getImageSize` is the scale percentage of the original asset (0-100).
   */
  getImageSize(this: BaseSupplementContext): string {
    return readContent(this).get('imageSize', 100) as string;
  },

  getImageUrl(this: BaseSupplementContext): string {
    return readContent(this).get('image') as string;
  },
  /**
   * Returns a `Location` object representing the location of the supplement.
   */
  getImageLocation(this: BaseSupplementContext): Location {
    const origin = constants.SUPPLEMENTS_ORIGIN;

    // This is necessary to encode URLs of older image supplements names that are not normalized
    const encodedPath = (readContent(this).get('image') as string | undefined)
      ?.split('/')
      .map(encodeURIComponent)
      .join('/');

    return {
      path: encodedPath,
      href: `${origin}/${encodedPath}`
    } as Location;
  },
  getGenerationMode(this: BaseSupplementContext): string | null {
    return readContent(this).get('generationMode', null) as string | null;
  },

  getImagePrompt(this: BaseSupplementContext): string {
    return readContent(this).get('imagePrompt') as string;
  },

  getImageStatus(this: BaseSupplementContext): string {
    return readContent(this).get('imageStatus', 'complete') as string;
  },

  getAudioText(this: BaseSupplementContext): string {
    return readContent(this).get('audioText') as string;
  },

  getAudioStatus(this: BaseSupplementContext): string {
    return readContent(this).get('audioStatus', 'complete') as string;
  },

  getVoiceId(this: BaseSupplementContext): string | null {
    return readContent(this).get('voiceId', null) as string | null;
  },

  getLanguageCode(this: BaseSupplementContext): string | null {
    return readContent(this).get('languageCode', null) as string | null;
  },

  getText(this: BaseSupplementContext): string {
    return readContent(this).get('text') as string;
  },

  getType(this: BaseSupplementContext): string {
    return readContent(this).get('type') as string;
  },

  getValueForContentAttribute(
    this: BaseSupplementContext,
    attribute: string,
    def: unknown
  ): unknown {
    return readContent(this).get(attribute, def);
  },

  isAttributeRequired(this: BaseSupplementContext, attribute: string): boolean {
    if (!this.getType()) {
      return false;
    }
    return requiredAttributesByType[this.getType()].includes(attribute);
  },

  setAlttext(
    this: BaseSupplementContext,
    value: unknown
  ): AuthoringSupplementModel | SupplementModel {
    const content = writeContent(this).set('alttext', value);
    return this.setField('content', content);
  },

  setAudio(
    this: BaseSupplementContext,
    value: unknown
  ): AuthoringSupplementModel | SupplementModel {
    const content = writeContent(this).set('audio', value);
    return this.setField('content', content);
  },

  setCaption(
    this: BaseSupplementContext,
    value: unknown
  ): AuthoringSupplementModel | SupplementModel {
    const content = writeContent(this).set('caption', value);
    return this.setField('content', content);
  },

  setCropValues(
    this: BaseSupplementContext,
    value: {
      x: number;
      y: number;
      width: number;
      height: number;
    }
  ): AuthoringSupplementModel | SupplementModel {
    const content = writeContent(this).merge(value);
    return this.setField('content', content);
  },

  setExcludeWhiteSpace(
    this: BaseSupplementContext,
    value: boolean
  ): AuthoringSupplementModel | SupplementModel {
    const content = writeContent(this).set('excludeWhiteSpace', value);
    return this.setField('content', content);
  },

  setHasTotal(
    this: BaseSupplementContext,
    value: boolean
  ): AuthoringSupplementModel | SupplementModel {
    const content = writeContent(this).set('hasTotal', value);
    return this.setField('content', content);
  },

  setHideLineNumbers(
    this: BaseSupplementContext,
    value: boolean
  ): AuthoringSupplementModel | SupplementModel {
    const content = writeContent(this).set('hideLineNumbers', value);
    return this.setField('content', content);
  },

  setShowLineNumbers(
    this: BaseSupplementContext,
    value: boolean
  ): AuthoringSupplementModel | SupplementModel {
    const content = writeContent(this).set('hideLineNumbers', !value);
    return this.setField('content', content);
  },

  setIgnoreWhiteSpace(
    this: BaseSupplementContext,
    value: boolean
  ): AuthoringSupplementModel | SupplementModel {
    const content = writeContent(this).set('ignoreWhiteSpace', value);
    return this.setField('content', content);
  },

  setShowWhiteSpace(
    this: BaseSupplementContext,
    value: boolean
  ): AuthoringSupplementModel | SupplementModel {
    const content = writeContent(this).set('ignoreWhiteSpace', !value);
    return this.setField('content', content);
  },

  setImageSize(
    this: BaseSupplementContext,
    value: unknown
  ): AuthoringSupplementModel | SupplementModel {
    const content = writeContent(this).set('imageSize', value);
    return this.setField('content', content);
  },

  setGenerationMode(
    this: BaseSupplementContext,
    value: unknown
  ): AuthoringSupplementModel | SupplementModel {
    const content = writeContent(this).set('generationMode', value);
    return this.setField('content', content);
  },

  setImagePrompt(
    this: BaseSupplementContext,
    value: unknown
  ): AuthoringSupplementModel | SupplementModel {
    const content = writeContent(this).set('imagePrompt', value);
    return this.setField('content', content);
  },

  setImageUrl(
    this: BaseSupplementContext,
    value: unknown
  ): AuthoringSupplementModel | SupplementModel {
    const content = writeContent(this)
      .set('image', value)
      // if we manually set an image URL, clear out any existing prompt
      .set('imagePrompt', '');
    return this.setField('content', content);
  },

  setAudioText(
    this: BaseSupplementContext,
    value: unknown
  ): AuthoringSupplementModel | SupplementModel {
    const content = writeContent(this).set('audioText', value);
    return this.setField('content', content);
  },

  setVoiceId(
    this: BaseSupplementContext,
    value: unknown
  ): AuthoringSupplementModel | SupplementModel {
    const content = writeContent(this).set('voiceId', value || null);
    return this.setField('content', content);
  },

  setLanguageCode(
    this: BaseSupplementContext,
    value: unknown
  ): AuthoringSupplementModel | SupplementModel {
    const content = writeContent(this).set('languageCode', value || null);
    return this.setField('content', content);
  },

  setText(this: BaseSupplementContext, value: unknown): AuthoringSupplementModel | SupplementModel {
    const content = writeContent(this).set('text', value);
    return this.setField('content', content);
  },

  setType(this: BaseSupplementContext, value: unknown): AuthoringSupplementModel | SupplementModel {
    const content = writeContent(this).set('type', value);
    return this.setField('content', content);
  },

  setLineNumberIncrement(
    this: BaseSupplementContext,
    value: number
  ): AuthoringSupplementModel | SupplementModel {
    const content = writeContent(this).set('lineNumberIncrement', value);
    return this.setField('content', content);
  },

  setLineNumberAlignment(
    this: BaseSupplementContext,
    value: string
  ): AuthoringSupplementModel | SupplementModel {
    const content = writeContent(this).set('lineNumberAlignment', value);
    return this.setField('content', content);
  },

  setExcludeHeaders(
    this: BaseSupplementContext,
    value: boolean
  ): AuthoringSupplementModel | SupplementModel {
    const content = writeContent(this).set('excludeHeaders', value);
    return this.setField('content', content);
  },

  setSetMaxHeight(
    this: BaseSupplementContext,
    value: boolean
  ): AuthoringSupplementModel | SupplementModel {
    const content = writeContent(this).set('setMaxHeight', value);
    return this.setField('content', content);
  },

  setTitle(this: BaseSupplementContext, value: string): AuthoringSupplementModel | SupplementModel {
    const content = writeContent(this).set('title', value);
    return this.setField('content', content);
  }
};
